import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Subscription, catchError, debounceTime, of, tap } from 'rxjs';
import { isRealChange } from '../../helper/simple-changes-typed';
import { IAutoCompleteScrollEvent } from '../../directives/infinite-scroll.directive';
import { FormControlEx } from '../forms/form-control';
import { AutoCompleteSelectionItem } from '../autocomplete-remote/autocomplete-selection-item';
import { IAutoCompleteService } from '../autocomplete-remote/autocomplete-service.interface';

@Component({
    selector: 'app-autocomplete-remote-multi',
    templateUrl: './autocomplete-remote-multi.component.html',
    styleUrls: ['./autocomplete-remote-multi.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AutocompleteRemoteMultiComponent<TData = any, TOptions = any> implements OnInit, OnChanges, OnDestroy {
  private _control: FormControlEx;
  // Accept AbstractControl so templates can bind `form.controls['x']`; the template
  // and this class use FormControlEx members (caption, isReadonly), so expose as FormControlEx.
  @Input() set control(value: AbstractControl) {
    this._control = value as FormControlEx;
  }
  get control(): FormControlEx {
    return this._control;
  }
  @Input() service: IAutoCompleteService<TData, TOptions>;
  @Input() defaultSearch: string = '*';
  @Input() serviceOptions: TOptions;
  @Input() updateOnOptionsChange: boolean = false;
  @Input() clearOthers: boolean;

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autoTrigger: MatAutocompleteTrigger;

  searchControl = new FormControl<string>('');
  selectedItems: AutoCompleteSelectionItem<TData>[] = [];
  searchResults: AutoCompleteSelectionItem<TData>[] = [];
  pageLoaded = new EventEmitter<number>();

  private subscription = new Subscription();
  private loadSub: Subscription;
  private selectionLoadSub = new Subscription();
  private selectionSyncVersion = 0;
  private page: number = 0;
  private allPagesLoaded: boolean = false;
  private currentSearch: string;

  private get isReadonly(): boolean {
    return this.control?.isReadonly;
  }

  get displayItems(): AutoCompleteSelectionItem<TData>[] {
    const selectedValues = new Set(this.selectedItems.map(i => i.value));
    const searchResultValues = new Set(this.searchResults.map(i => i.value));
    const missingSelected = this.selectedItems.filter(i => !searchResultValues.has(i.value));
    const selectedInResults = this.searchResults.filter(i => selectedValues.has(i.value));
    const unselectedInResults = this.searchResults.filter(i => !selectedValues.has(i.value));
    return [...missingSelected, ...selectedInResults, ...unselectedInResults];
  }

  isSelected(item: AutoCompleteSelectionItem<TData>): boolean {
    return this.selectedItems.some(i => i.value === item.value);
  }

  ngOnInit(): void {
    this.syncSelectedItemsFromControl(this.control.value);

    this.subscription.add(this.control.valueChanges.subscribe(value => this.syncSelectedItemsFromControl(value)));

    this.subscription.add(
      this.searchControl.valueChanges.pipe(debounceTime(400)).subscribe(search => {
        this.loadResults(search || this.defaultSearch);
      })
    );

    this.loadResults(this.defaultSearch);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!isRealChange(changes.serviceOptions)) {
      return;
    }

    this.loadResults(this.currentSearch || this.defaultSearch);

    if (this.updateOnOptionsChange) {
      this.syncSelectedItemsFromControl(this.control?.value, true);
    } else if (this.selectedItems.length) {
      this.searchResults = this.selectedItems;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.loadSub?.unsubscribe();
    this.selectionLoadSub.unsubscribe();
  }

  onFocus(): void {
    if (this.isReadonly) {
      return;
    }
    this.searchControl.setValue('', { emitEvent: false });
    this.loadResults(this.defaultSearch);
  }

  onBlur(): void {
    setTimeout(() => {
      if (!this.autoTrigger?.panelOpen) {
        this.updateDisplayText();
      }
    }, 300);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.isReadonly) {
      return;
    }
    const itemName = event.option.value as string;
    const item = this.displayItems.find(i => i.name === itemName);
    if (item) {
      this.toggleItem(item);
    }
    setTimeout(() => {
      this.searchInput.nativeElement.value = '';
      this.searchControl.setValue(''); // emit to cancel pending debounced search
      this.loadResults(this.defaultSearch);
      this.autoTrigger.openPanel();
    });
  }

  toggleItem(item: AutoCompleteSelectionItem<TData>): void {
    if (this.isReadonly) {
      return;
    }
    const index = this.selectedItems.findIndex(i => i.value === item.value);
    if (index >= 0) {
      this.selectedItems = this.selectedItems.filter(i => i.value !== item.value);
    } else {
      this.selectedItems = [...this.selectedItems, item];
    }
    this.updateControlValue();
  }

  clearAll(): void {
    if (this.isReadonly) {
      return;
    }
    this.selectedItems = [];
    this.searchControl.setValue('', { emitEvent: false });
    this.updateControlValue();
  }

  addonClicked(): boolean {
    if (this.isReadonly) {
      return false;
    }
    if (this.clearOthers && this.control.parent) {
      for (const controlName of Object.keys(this.control.parent.controls)) {
        const currentControl = this.control.parent.get(controlName);
        if (currentControl !== this.control && !(currentControl as FormControlEx).isReadonly) {
          currentControl.setValue(undefined);
        }
      }
    }
    return false;
  }

  get showClearOthers(): boolean {
    return (
      this.clearOthers && !!this.control.parent && Object.keys(this.control.parent.controls).length > 1 && this.selectedItems.length > 0
    );
  }

  private updateControlValue(): void {
    if (this.isReadonly) {
      return;
    }
    const values = this.selectedItems.map(i => i.value);
    this.control.setValue(values);
    this.control.markAsDirty();
  }

  private updateDisplayText(): void {
    const text = this.selectedItems.map(i => i.shortName || i.name).join(', ');
    this.searchControl.setValue(text, { emitEvent: false });
  }

  private syncSelectedItemsFromControl(value: string[] | string | null | undefined, forceReload: boolean = false): void {
    this.selectionLoadSub.unsubscribe();
    this.selectionLoadSub = new Subscription();
    const syncVersion = ++this.selectionSyncVersion;

    const controlValues = Array.isArray(value) ? value : value ? [value] : [];

    if (!controlValues.length) {
      this.selectedItems = [];
      this.searchControl.setValue('', { emitEvent: false });
      return;
    }

    const ids = controlValues.filter(id => !!id);
    if (!ids.length) {
      this.selectedItems = [];
      this.searchControl.setValue('', { emitEvent: false });
      return;
    }

    const existingItems = new Map(this.selectedItems.map(item => [item.value, item]));
    this.selectedItems = forceReload
      ? []
      : ids
          .map(id => existingItems.get(id))
          .filter((item): item is AutoCompleteSelectionItem<TData> => !!item);
    this.updateDisplayText();

    for (const id of ids) {
      if (this.selectedItems.some(i => i.value === id)) continue;

      this.selectionLoadSub.add(
        this.service.getSelectionItem(id, { selectedItem: null, options: this.serviceOptions }).subscribe(item => {
          if (syncVersion !== this.selectionSyncVersion || !item || this.selectedItems.some(i => i.value === item.value)) return;

          this.selectedItems = this.sortSelectedItemsByControlValue([...this.selectedItems, item], ids);
          this.updateDisplayText();
        })
      );
    }
  }

  private sortSelectedItemsByControlValue(
    items: AutoCompleteSelectionItem<TData>[],
    values: string[]
  ): AutoCompleteSelectionItem<TData>[] {
    return [...items].sort((a, b) => values.indexOf(a.value ?? '') - values.indexOf(b.value ?? ''));
  }

  onOptionsScroll(ev: IAutoCompleteScrollEvent): void {
    if (ev.action === 'nextpage') {
      this.nextPage();
    }
  }

  private nextPage(): void {
    if (!this.page || this.allPagesLoaded) return;
    this.loadResults(this.currentSearch || this.defaultSearch, this.page + 1);
  }

  private loadResults(search: string, page: number = 1): void {
    this.loadSub?.unsubscribe();
    if (page === 1) {
      this.allPagesLoaded = false;
    }
    this.currentSearch = search;
    this.loadSub = this.service
      .getSelection(search, this.serviceOptions, page)
      .pipe(
        tap(items => {
          if (page === 1) {
            this.searchResults = items;
          } else {
            this.searchResults = [...this.searchResults, ...items];
          }
          this.page = page;
          if (items.length) {
            this.pageLoaded.emit(page);
          } else {
            this.allPagesLoaded = true;
          }
        }),
        catchError(error => {
          console.warn(error);
          this.searchResults = [];
          return of([]);
        })
      )
      .subscribe();
  }
}
